<template>
  <div class="volumeWeight" ref="scrollable">
    <h1 class="title_text padding page-title">
      Расчет веса по объёму
    </h1>

    <div class="headerBlock-select padding">
      <div class="switcherBox" style="gap: 12px;" v-if="isOwnSample">
        Впишите плотность г/см³
        <castom-input
          :defaultValue="0"
          style="width: 76px;"
          v-model="inputs.density"
          :required="isRequired"
          ref="resetDensity"
        />
      </div>
      <div class="switcherBox" style="gap: 10px;" v-if="!isOwnSample">
        Выберите пробу
        <castom-select
          :placeholder="'Проба'"
          :options="densityOptions"
          style="width: 80px;"
          v-model="inputs.density"
          :required="isRequired"
        />
      </div>
      <div class="switcherBox">
        Своя плотность
        <CastomSwitcher
          style="margin-bottom: 7px;"
          v-model="isOwnSample"
        />
      </div>
    </div>

    <div class="headerBlock-metal padding">
      <SelectMetal
        v-model="selectedMetal"
      />
    </div>

    <div class="inputs padding">
      <div class="inputs-item">
        <p>Объём заготовки см³</p>
        <castom-input
          fontSize="16px"
          v-model="inputs.volume"
          ref="resetVolume"
          :required="isRequired"
        />
      </div>
    </div>

    <button
      @click="calculateClick"
      class="button"
    >
      Рассчитать
    </button>

    <div class="footer flexColumn" v-if="weight">
      <div class="footer-container flexRow">
        <div class="footer-text fs14">
          Вес заготовки
        </div>
        <div class="footer-number fs14">
          {{ weight }}
        </div>
        <div class="footer-text fs14">
          г
        </div>
      </div>
      <div class="footer-container flexRow" v-if="metalCost">
        <div class="footer-text fs14">
          Стоимость металла
        </div>
        <div class="footer-number fs14">
          {{ formatPrice(metalCost) }}
        </div>
        <div class="footer-text fs14">
          ₽
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, watch } from "vue"
import CastomInput from "@/components/inputs/CastomInput"
import CastomSelect from "@/components/selects/CastomSelect"
import SelectMetal from "@/components/selects/SelectMetal"
import CastomSwitcher from "@/components/switchers/CastomSwitcher"
import metalDensity from "@/jsonData/metalDensity.json"
import { useMetalsStore } from "@/stores/metalsPrice/metalsStore"
import { formatPrice } from "@/helpers/helpers"
import { trackCalculation } from '@/helpers/calculationAnalytics'

const selectedMetal = ref('silver')
const isOwnSample = ref(false)
const isRequired = ref(false)

const resetVolume = ref('')
const resetDensity = ref('')

const scrollable = ref(null)
const metalsStore = useMetalsStore()

const inputs = reactive({
  volume: 0, // см3
  density: 0, // number | {name,value,density}
})

const weight = ref(0)

if (window.gtag) {
  window.gtag('event', 'volume-weight_calculator', {})
}

const densityOptions = computed(() => {
  if (isOwnSample.value) {
    return { density: inputs.density, name: 'Своя' }
  }
  return selectedMetal.value === 'silver' ? metalDensity.silver : metalDensity.gold
})

const densityValue = computed(() => {
  if (isOwnSample.value) return Number(inputs.density)
  return inputs.density?.density ? Number(inputs.density.density) : 0
})

const assayValue = computed(() => {
  if (isOwnSample.value) return null
  return inputs.density?.value ? Number(inputs.density.value) : null
})

const metalRate = computed(() => {
  if (!metalsStore?.data) return 0
  return selectedMetal.value === 'gold'
    ? Number(metalsStore.data.gold || 0)
    : Number(metalsStore.data.silver || 0)
})

const metalCost = computed(() => {
  if (isOwnSample.value) return null
  const w = Number(weight.value) || 0
  const rate = metalRate.value || 0
  const assay = assayValue.value
  if (!w || !rate || !assay) return null
  const cost = w * rate * (assay / 1000)
  return Number.isFinite(cost) ? cost.toFixed(2) : null
})

const isValid = computed(() => {
  return Number(inputs.volume) > 0 && densityValue.value > 0
})

const calculateClick = () => {
  setTimeout(() => {
    scrollToBottom()
  }, 500)

  isRequired.value = true
  if (!isValid.value) {
    weight.value = 0
    return
  }

  weight.value = parseFloat((Number(inputs.volume) * densityValue.value).toFixed(2))
  trackCalculation('volume_weight')
}

const scrollToBottom = () => {
  if (scrollable.value) {
    scrollable.value.scrollTo({
      top: scrollable.value.scrollHeight,
      behavior: 'smooth',
    })
  }
}

const resetResults = () => {
  weight.value = 0
  if (!inputs.volume && resetVolume.value?.resetValue) resetVolume.value.resetValue()
}

watch(selectedMetal, () => {
  resetResults()
  if (!isOwnSample.value) {
    inputs.density = 0
  }
})

watch(isOwnSample, () => {
  inputs.density = 0
  resetResults()
})
</script>

<style lang="scss" scoped>
.volumeWeight {
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  align-items: center;
  text-align: center;
  height: calc(100vh - 140px);
  width: 100%;
  justify-content: flex-start;
  padding-bottom: 10px;
}

.inputs {
  margin-top: 34px;
  color: white;
  width: 100%;
  font-size: 16px;
  padding: 15px 0 35px 0 !important;
  &-item{
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
  }
}

.headerBlock {
  &-select {
    width: 100%;
    justify-content: space-between;
    display: flex;
    align-items: center;
    padding-right: 20px;
    padding-left: 20px;
  }
  &-metal {
    width: 100%;
  }
}

.switcherBox {
  text-align: center;
  font-size: 14px;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 19px;
  align-items: center;
}

.footer {
  margin-top: 20px;
  margin-bottom: 20px;
  border-radius: 25px;
  border: 2px solid #fff;
  height: auto;
  padding: 10px;
  width: 90%;
  box-shadow: 0 2px 2px 2px rgba(0, 0, 0, 0.25);
  background: linear-gradient(180deg, rgba(181, 162, 189, 0.33) 0%, rgba(26, 21, 28, 0.33) 100%);
  gap: 7px;
  &-container {
    color: white;
    justify-content: flex-start
  }
  &-text {
    font-size: 18px;
    font-weight: bold;
    margin-left: 10px;
  }
  &-number {
    font-size: 18px;
    font-weight: bold;
    margin-left: auto;
  }
}

.fs14 {
  font-size: 14px;
}

.padding {
  border: 2px solid white;
  border-left: none;
  border-right: none;
  border-bottom: none;
  padding: 10px 0;
}

.flexColumn {
  display: flex;
  flex-direction: column;
}

.flexRow {
  display: flex;
  flex-direction: row;
}

.title_text {
  color: white;
  width: 100%;
}

.button {
  background: rgba(0, 0, 0, 0.22);
  color: rgba(255, 255, 255, 0.98);
  width: 100%;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: 0.2px;
  border: 1px solid rgba(255, 255, 255, 0.45);
  cursor: pointer;
  padding: 14px 20px;
  margin-top: 20px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  transition: transform 0.08s ease, filter 0.18s ease;
  user-select: none;
}

.button:active {
  transform: translateY(1px) scale(0.99);
  filter: brightness(0.96);
}

.button:focus-visible {
  outline: 2px solid rgba(255, 255, 255, 0.95);
  outline-offset: 2px;
}
</style>

